import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, type DrawerProps, Space, Spin, Form, Result } from 'antd';
import { useField } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type DealerListDataFragment } from '../../../../api/fragments/DealerListData';
import { useListDealersQuery } from '../../../../api/queries/listDealers';
import DrawerWithAutoWidth from '../../../DrawerWithAutoWidth';
import { type SelectFieldProps } from '../../SelectField';
import { useTranslationDrawer } from '../../TranslatedInputField/TranslationDrawer';
import TranslatedMarkdownField from '../../TranslatedMarkdownField';
import { useAddDealershipModal } from '../AddDealershipModal';
import { type DealershipItem } from '../DealershipSelectField/DealershipDrawer';
import { retrieveDealerbyId } from '../shared';

export type DealershipTranslatedMarkdownDrawerProps = Pick<DrawerProps, 'title' | 'visible' | 'onClose'> &
    Pick<SelectFieldProps, 'options'> & {
        name: string;
        defaultValueName: string;
        dealers?: DealerListDataFragment[];
        renderField?: (
            item: DealershipItem,
            onDelete: () => void,
            onOpenTranslation: () => void,
            options: SelectFieldProps['options']
        ) => JSX.Element;
    };

const DealershipTranslatedMarkdownDrawer = ({
    onClose,
    title,
    visible,
    name,
    defaultValueName,
    dealers: dealersFromProps,
    renderField = renderInputField,
    options,
}: DealershipTranslatedMarkdownDrawerProps) => {
    const { t } = useTranslation(['translationField']);

    const [{ value: dealerships }, , { setValue }] = useField<{ dealerId: string; value: object }[]>({
        name,
    });
    const { data, loading } = useListDealersQuery({
        fetchPolicy: 'cache-and-network',
        skip: !!dealersFromProps,
    });
    const translatedDrawer = useTranslationDrawer();

    const dealersFromQuery = data?.page?.items;

    const allDealers = useMemo(() => dealersFromProps || dealersFromQuery || [], [dealersFromProps, dealersFromQuery]);
    const remainingDealers = useMemo(() => {
        if (!dealerships) {
            return allDealers;
        }

        return allDealers.filter(dealer => !dealerships.some(dealership => dealership.dealerId === dealer.id));
    }, [allDealers, dealerships]);

    const actions = useMemo(() => {
        const currentDealers = dealerships || [];

        return {
            add: (dealerId: string) =>
                setValue([...currentDealers, { dealerId, value: { defaultValue: '', overrides: [] } }]),
            delete: (index: number) => {
                const newValue = [...currentDealers];
                newValue.splice(index, 1);
                setValue(newValue);
            },
        };
    }, [setValue, dealerships]);

    const addModal = useAddDealershipModal(remainingDealers, actions.add);

    const cleanedDealerships = useMemo((): DealershipItem[] => {
        if (!dealerships) {
            return [];
        }

        return dealerships
            .map((dealership, index) => {
                // if there is no dealer found based on id, it will return null
                const dealer = retrieveDealerbyId(allDealers, dealership.dealerId);

                if (!dealer) {
                    return null;
                }

                return { ...dealership, dealer, name: `${name}[${index}].value`, index, options };
            })
            .filter(Boolean)
            .toSorted((a, b) => a.dealer.displayName.localeCompare(b.dealer.displayName));
    }, [dealerships, allDealers, name, options]);

    const content = (
        <Space direction="vertical" style={{ width: '100%' }}>
            {cleanedDealerships.length > 0 && (
                <Form layout="vertical">
                    {cleanedDealerships.map(dealership =>
                        renderField(dealership, () => actions.delete(dealership.index), translatedDrawer.open, options)
                    )}
                </Form>
            )}
            {remainingDealers.length > 0 && (
                <Button icon={<PlusOutlined />} onClick={addModal.open} type="dashed" block>
                    {t('dealershipField:default.addDealer')}
                </Button>
            )}
            {addModal.render()}
            {!cleanedDealerships.length && !remainingDealers.length && (
                <Result status="info" subTitle={t('dealershipField:noDealerRegistered')} />
            )}
        </Space>
    );

    return (
        <DrawerWithAutoWidth onClose={onClose} placement="right" title={title} visible={visible} destroyOnClose>
            <Spin spinning={loading}>
                <Form layout="vertical">
                    <TranslatedMarkdownField label="Default Value" name={defaultValueName} required={false} />
                </Form>

                {content}
            </Spin>
        </DrawerWithAutoWidth>
    );
};

export const renderInputField: DealershipTranslatedMarkdownDrawerProps['renderField'] = (
    market,
    onDelete,
    onOpenTranslation
) => {
    const ButtonList = <Button icon={<DeleteOutlined />} onClick={onDelete} size="small" type="text" />;

    return (
        <TranslatedMarkdownField
            key={market.dealerId}
            addMoreButton={ButtonList}
            label={market.dealer.displayName}
            name={market.name}
            required={false}
        />
    );
};

export default DealershipTranslatedMarkdownDrawer;

export const useDealershipTranslatedMarkdownDrawer = () => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: (
            props: Pick<
                DealershipTranslatedMarkdownDrawerProps,
                'name' | 'title' | 'renderField' | 'dealers' | 'options'
            >
        ) => (
            <DealershipTranslatedMarkdownDrawer
                {...props}
                defaultValueName={`${props.name}.defaultValue`}
                name={`${props.name}.overrides`}
                onClose={actions.close}
                visible={visible}
            />
        ),
    };
};
